/**
 * Y.Doc layout for a Fabled Edit (runtime-only — the persisted interchange
 * format stays the plain JSON of docs/timeline-format.md).
 *
 * Layout principle: everything is id-keyed in Y.Maps; ORDER IS A FIELD, never
 * array position. Y.Array moves are delete+reinsert and lose concurrent edits
 * inside the moved element, so:
 *
 *   meta:    Y.Map { version: number, settings: <plain JSON EditSettings> }
 *   sources: Y.Map srcId -> <plain JSON Source, pandora url always null>
 *   tracks:  Y.Map trackId -> Y.Map {
 *              <scalar track fields as individual keys>,
 *              ord:   fractional-index string (track z/display order),
 *              clips: Y.Map clipId -> Y.Map {
 *                <scalar clip fields as individual keys; transitionIn/style
 *                 are single plain-JSON leaves>,
 *                transform|position: Y.Map paramName -> <plain JSON Param>,
 *                effects: Y.Map effectId -> { ord, effect: <sans id> },
 *              }
 *            }
 *
 * Clip order within a track is DERIVED from `start` (with id tiebreak) — the
 * same rule the format's normalization already uses — so it needs no ord.
 * Track order and effect order are authored, hence fractional `ord` fields.
 * Params (number | {keyframes}) are deliberately single LWW leaves: keyframe
 * arrays are replaced wholesale, not merged per-keyframe.
 */

/** Origin tag for local, undoable mutations (tracked by the UndoManager). */
export const LOCAL_ORIGIN = 'fabled-local';
/** Origin for local mutations that must NOT create undo entries. */
export const NO_UNDO_ORIGIN = 'fabled-local-noundo';
/** Origin used when applying an accepted AI proposal (undoable as one step). */
export const AGENT_ORIGIN = 'fabled-agent';
/** Origin for chat writes — never undoable, never a timeline change. */
export const CHAT_ORIGIN = 'fabled-chat';
/**
 * Origin for writes to the `agent` root (run status, proposal records).
 * Distinct from AGENT_ORIGIN: that one marks an *applied* proposal changing the
 * timeline and IS undoable; this one marks bookkeeping that must never be.
 */
export const AGENT_STATE_ORIGIN = 'fabled-agent-state';

export const ROOT_META = 'meta';
export const ROOT_SOURCES = 'sources';
export const ROOT_TRACKS = 'tracks';

/**
 * Collaborator chat (Y.Array of message leaves). NOT part of the edit format:
 * the snapshot writer reads only the three roots above, so chat never reaches
 * `Project.timeline` and cannot fail validation, and the UndoManager scopes to
 * those same three, so chat is not undoable.
 *
 * Adding a root here looks like it breaks the "only the server seeds a room"
 * rule, and does not: that rule is about id-keyed *content* built independently
 * by two peers. Root types are addressed by name and are deterministic —
 * `doc.getArray(ROOT_CHAT)` refers to the same root on every peer.
 */
export const ROOT_CHAT = 'chat';

/**
 * Assistant state (Y.Map): run status + proposal records. A sibling root for
 * the same reasons as chat — the snapshot writer reads only meta/sources/tracks,
 * and the UndoManager scopes to those three, so nothing here is persisted into
 * `Project.timeline` or made undoable.
 *
 * Proposals live here rather than in the chat message that shows them because
 * their status changes (pending → applied/discarded/conflict) and chat entries
 * are append-only leaves. The chat message holds only the proposal id.
 */
export const ROOT_AGENT = 'agent';

/** Clip-map keys that hold a nested Y.Map of Params rather than a leaf. */
export const PARAM_MAP_KEYS = new Set(['transform', 'position']);

// ---------------------------------------------------------------------------
// Fractional indexing (Figma-style midpoint strings, base36).
// ---------------------------------------------------------------------------

const ORD_DIGITS = '0123456789abcdefghijklmnopqrstuvwxyz';
const ORD_BASE = 36;

/**
 * A string strictly between `a` and `b` in lexicographic order.
 * `a = ''` means "before everything"; `b = null` means "after everything".
 * Results never end in the zero digit, so there is always room below any ord.
 */
function ordDigit(s: string, i: number): number {
  if (i >= s.length) return 0;
  const d = ORD_DIGITS.indexOf(s[i]);
  // Ords are read back from a document any client can write, so say which value
  // was bad rather than letting -1 corrupt the arithmetic silently.
  if (d < 0) throw new Error(`ordBetween: '${s}' is not a valid ord`);
  return d;
}

export function ordBetween(a: string, b: string | null): string {
  if (b !== null && a >= b) {
    throw new Error(`ordBetween: '${a}' must sort before '${b}'`);
  }
  let result = '';
  let hi = b;
  for (let i = 0; ; i++) {
    const da = ordDigit(a, i);
    if (hi !== null && i >= hi.length) {
      // The upper bound ran out while we were still matching it digit for
      // digit, so `result` equals `hi` and ANY suffix sorts after it. Nothing
      // fits that also honours the never-end-in-zero rule. Ords generated here
      // can never trigger this; one that does came from outside.
      throw new Error(`ordBetween: no ord fits between '${a}' and '${b}'`);
    }
    const db = hi !== null ? ordDigit(hi, i) : ORD_BASE;
    if (da === db) {
      result += ORD_DIGITS[da];
      continue;
    }
    if (db - da > 1) {
      return result + ORD_DIGITS[Math.floor((da + db) / 2)];
    }
    // adjacent digits: keep the lower one and continue with an open upper bound
    result += ORD_DIGITS[da];
    hi = null;
  }
}

/**
 * Make stored ords consistent with a desired id order, touching as few
 * entries as possible: ids whose ord already increases are left alone; the
 * rest get a fresh ord between their neighbours. Missing ords count as broken.
 */
export function ensureOrds(
  ids: string[],
  getOrd: (id: string) => string | undefined,
  setOrd: (id: string, ord: string) => void,
): void {
  let prev = '';
  for (let i = 0; i < ids.length; i++) {
    const cur = getOrd(ids[i]);
    if (cur !== undefined && cur > prev) {
      prev = cur;
      continue;
    }
    let bound: string | null = null;
    for (let j = i + 1; j < ids.length; j++) {
      const o = getOrd(ids[j]);
      if (o !== undefined && o > prev) {
        bound = o;
        break;
      }
    }
    let ord: string;
    try {
      ord = ordBetween(prev, bound);
    } catch {
      // The stored bound is unusable — malformed, or packed so tightly that
      // nothing valid fits beneath it. Both mean a document written by
      // something other than this code. Appending after `prev` always succeeds
      // and the neighbour gets renumbered next pass; losing one item's exact
      // position beats throwing out of a reconcile.
      ord = ordBetween(prev, null);
    }
    setOrd(ids[i], ord);
    prev = ord;
  }
}
